/**
 * @module components/DescriptionSetting/EditDescriptionModal
 * @description 表格字段描述编辑弹窗（逻辑对齐 ChartsDrop/SubsetFormComp EditDescriptionModal）
 */

import React, { useEffect, useState } from 'react';
import { useIntl } from 'umi';
import { Button, Form, Modal, TextArea } from 'tea-component';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { IColumnFilter } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import type {
  IDescriptionSetting,
  IDescriptionColumnItem,
} from '../../types/settings/description'

export interface IEditDescriptionModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  filters?: IColumnInfo[];
  dimensions?: IDescriptionColumnItem[];
  metrics?: IDescriptionColumnItem[];
  initValues?: IDescriptionSetting;
  onChange?: (value: IDescriptionSetting) => void;
}

const EditDescriptionModal: React.FC<IEditDescriptionModalProps> = ({
  visible,
  setVisible,
  filters,
  dimensions,
  metrics,
  initValues,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const [description, setDescription] = useState<IDescriptionSetting>(
    initValues || {},
  );
  const compareFilters = filters?.find(
    item => (item as IColumnFilter)?.compareTimes?.length > 0,
  ) as IColumnFilter | undefined;

  useEffect(() => {
    if (visible) {
      setDescription(initValues || {});
    }
  }, [initValues, visible]);

  const handleConfirm = (): void => {
    const newValue = Object.keys(description).reduce<IDescriptionSetting>(
      (acc, key) => {
        if (description[key]?.desc?.trim()) {
          acc[key] = { ...description[key] };
        }
        return acc;
      },
      {},
    );
    onChange?.(newValue);
    setVisible(false);
  };

  return (
    <Modal
      visible={visible}
      caption={formatMessage({ id: 'dbl_chart_description_edit_title' })}
      onClose={() => setVisible(false)}
      className="dbl-chart-setting-desc-modal"
    >
      <Modal.Body>
        <div className="dbl-chart-description-edit-waring">
          {formatMessage({ id: 'dbl_chart_description_edit_waring' })}
        </div>
        <Form>
          {dimensions?.map(item => {
            const key = `${item.table_code || 't0'}_${item.column_name}_1`;
            const label =
              compareFilters?.id === item?.id
                ? formatMessage({ id: 'dbl_time_compare_label' })
                : item.column_display;
            return (
              <Form.Item key={key} label={label}>
                <TextArea
                  className="dbl-chart-setting-desc-modal-textarea"
                  maxLength={2560}
                  value={description?.[key]?.desc || ''}
                  onChange={(text: string) => {
                    setDescription(prev => ({
                      ...prev,
                      [key]: {
                        column_name: item.column_name,
                        table_id: item.table_id,
                        table_code: item.table_code || 't0',
                        desc: text,
                        column_role: 1,
                      },
                    }));
                  }}
                  placeholder={formatMessage({
                    id: 'dbl_chart_description_edit_placeholder',
                  })}
                />
              </Form.Item>
            );
          })}
          {metrics?.map(item => {
            const key = `${item.table_code || 't0'}_${item.column_name}_0`;
            return (
              <Form.Item key={key} label={item.column_display}>
                <TextArea
                  className="dbl-chart-setting-desc-modal-textarea"
                  maxLength={2560}
                  value={description?.[key]?.desc || ''}
                  onChange={(text: string) => {
                    setDescription(prev => ({
                      ...prev,
                      [key]: {
                        column_name: item.column_name,
                        table_id: item.table_id,
                        table_code: item.table_code || 't0',
                        desc: text,
                        column_role: 0,
                      },
                    }));
                  }}
                  placeholder={formatMessage({
                    id: 'dbl_chart_description_edit_placeholder',
                  })}
                />
              </Form.Item>
            );
          })}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" onClick={handleConfirm}>
          {formatMessage({ id: 'dbl_common_confirm' })}
        </Button>
        <Button type="weak" onClick={() => setVisible(false)}>
          {formatMessage({ id: 'dbl_common_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default React.memo(EditDescriptionModal);
